import React from 'react';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { publicAssets } from '@ff-os/pdp-shared-redux/apis/publicAssets';
import { selectLanguage } from '@ff-os/pdp-shared-redux/slices/globalSlice';
import { AspectRatio } from '@ff-os/shared-react/components/aspect-ratio/AspectRatio';
import { Card } from '@ff-os/shared-react/components/card/Card';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid, GridLayout } from '@ff-os/shared-react/components/grid/Grid';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { forWeb } from '@ff-os/shared-utils/asset-urls';
import { selectAspectRatioByVariableId } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpDispatch, GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { noop } from 'lodash';
import { selectAssetUrlById, selectCollectionIdById, selectIsOptionalById, selectOpenByDefaultById, updateAssetUrlByIds } from '../_redux';
import { SvgInputRemoveSelection } from './SvgInputRemoveSelection';
import styles from './SvgInputOptionGrid.module.scss';

export const _SvgInputOptionGrid = ({
    variableIds, collectionId,
    selectedAssetUrl, isOptional,
    language, gridLayout,
    onClick
}: Props) => {

    const urls = publicAssets.useGetUrlsInCollectionQuery(collectionId);

    return (
        <Grid gap='s' {...gridLayout} wrap>
            {isOptional && (
                <Cell>
                    <SvgInputRemoveSelection
                        variableIds={variableIds}
                        active={!urls.isLoading && !selectedAssetUrl} />
                </Cell>
            )}

            {urls.isLoading && (
                <Cell>
                    <Card onClick={noop}>
                        <AspectRatio>
                            <Spinner overlaySize='full' spinnerSize='small' />
                        </AspectRatio>
                    </Card>
                </Cell>
            )}

            {urls.isSuccess && urls.data?.map(url => (
                <Cell key={url}>
                    <Card onClick={() => onClick(url)} active={url === selectedAssetUrl}>
                        <img
                            crossOrigin='anonymous'
                            width={300} height={300}
                            src={forWeb(url, { w: 300, h: 300, language })}
                            className={styles.image} alt='' />
                    </Card>
                </Cell>
            ))}
        </Grid>
    );
};

type Props = {
    variableIds: string[];
    collectionId: string;
    selectedAssetUrl: string;
    isOptional: boolean;
    language: string;
    gridLayout: GridLayout;
    onClick: (url: string) => void;
};

const mapStateToProps = (state: GiftPdpState, ownProps: { variableIds: string[] }) => ({
    isOptional: selectIsOptionalById(state, ownProps.variableIds[0])!,
    collectionId: selectCollectionIdById(state, ownProps.variableIds[0])!,
    selectedAssetUrl: selectAssetUrlById(state, ownProps.variableIds[0])!,
    openByDefault: selectOpenByDefaultById(state, ownProps.variableIds[0])!,
    gridLayout: selectLayout(state, ownProps.variableIds[0]),
    language: selectLanguage(state)
});

const mapDispatchToProps = (dispatch: GiftPdpDispatch, ownProps: { variableIds: string[] }) => ({
    onClick: (svgUrl: string) => dispatch(updateAssetUrlByIds(ownProps.variableIds, svgUrl))
});

export const SvgInputOptionGrid = connect(
    mapStateToProps,
    mapDispatchToProps
)(_SvgInputOptionGrid);

const selectLayout = createSelector(
    (state: GiftPdpState, id: string) => selectAspectRatioByVariableId(state, id),
    (aspectRatio): GridLayout => {
        if (aspectRatio && aspectRatio > 1.5) {
            return {
                xs: 'full',
                md: '1/2'
            };
        } else {
            return {
                xs: '1/4',
                md: '1/5'
            };
        }
    });